import { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, ChevronDown } from "lucide-react";
import Swal from "sweetalert2";
import {
  GetPestControl,
  DeletePestControl,
  PestControlData,
} from "../../services/pestcontrol";
import { fetchdropdownMall } from "../../services/dropdown";
import { cx } from "../../utils";
import PestControlForm from "./components/PestControlForm";

interface MallOption {
  value: string;
  label: string;
}

type SortKey = "TenantName" | "VendorName" | "ControlOn";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2021;
  const years: number[] = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

const formatDate = (dateString: string) => {
  if (!dateString) return "-";

  const match = dateString.match(new RegExp("/Date\\((\\d+)\\)/"));
  if (match) {
    const timestamp = parseInt(match[1]);
    const date = new Date(timestamp);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return "-";
};

export default function PestControl() {
  const [pestControlData, setPestControlData] = useState<PestControlData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("ControlOn");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [mallOptions, setMallOptions] = useState<MallOption[]>([]);
  const [selectedMall, setSelectedMall] = useState<string>("");
  const [mallSearchQuery, setMallSearchQuery] = useState("");
  const [isMallDropdownOpen, setIsMallDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState<PestControlData | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchMallOptions = async () => {
      try {
        const response = await fetchdropdownMall(0);
        if (response && response.dropdownClassDatas) {
          const options = response.dropdownClassDatas.map(
            (mall: { DataId: number; DataLabel: string }) => ({
              value: mall.DataId.toString(),
              label: mall.DataLabel,
            })
          );
          setMallOptions(options);

          const storedMallId =
            localStorage.getItem("mall_id") ||
            sessionStorage.getItem("mall_id");
          if (
            storedMallId &&
            options.find((opt: MallOption) => opt.value === storedMallId)
          ) {
            setSelectedMall(storedMallId);
          } else {
            setSelectedMall("");
            localStorage.removeItem("mall_id");
          }
        }
      } catch (error) {
        console.error("Error fetching mall options:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch mall options",
        });
      }
    };
    fetchMallOptions();
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedMall || selectedYear === 0 || selectedMonth === 0) return;
    setIsLoading(true);
    try {
      const response = await GetPestControl(
        0,
        parseInt(selectedMall),
        0,
        selectedYear,
        selectedMonth
      );
      if (response?.pestControlDatas) {
        setPestControlData(response.pestControlDatas);
      } else {
        setPestControlData([]);
      }
    } catch (error) {
      console.error("Error fetching pest control data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load pest control data",
      });
      setPestControlData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMall, selectedYear, selectedMonth]);

  useEffect(() => {
    if (selectedMall && selectedYear !== 0 && selectedMonth !== 0) {
      console.log("PestControl: Triggering fetch", { selectedMall, selectedYear, selectedMonth });
      fetchData();
    }
  }, [selectedMall, selectedYear, selectedMonth, fetchData]);

  const filteredMallOptions = useMemo(() => {
    if (!mallSearchQuery) return mallOptions;
    return mallOptions.filter((option) =>
      option.label.toLowerCase().includes(mallSearchQuery.toLowerCase())
    );
  }, [mallOptions, mallSearchQuery]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return pestControlData.filter(
      (item) =>
        item.TenantName?.toLowerCase().includes(q) ||
        item.VendorName?.toLowerCase().includes(q) ||
        item.VendorPhone?.toLowerCase().includes(q) ||
        item.Notes?.toLowerCase().includes(q)
    );
  }, [pestControlData, searchTerm]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      let aVal: string, bVal: string;
      if (sortKey === "TenantName") {
        aVal = a.TenantName?.toLowerCase() ?? "";
        bVal = b.TenantName?.toLowerCase() ?? "";
      } else if (sortKey === "VendorName") {
        aVal = a.VendorName?.toLowerCase() ?? "";
        bVal = b.VendorName?.toLowerCase() ?? "";
      } else {
        aVal = a.ControlOn ?? "";
        bVal = b.ControlOn ?? "";
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * perPage;
  const end = Math.min(start + perPage, total);
  const pageRows = sorted.slice(start, end);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await DeletePestControl(id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Pest control record has been deleted.",
          showConfirmButton: false,
          timer: 1500,
        });
        fetchData();
      } catch (error) {
        console.error("Error deleting pest control record:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete pest control record",
        });
      }
    }
  };

  const handleEdit = (item: PestControlData) => {
    setEditData(item);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    if (!selectedMall) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a mall first",
      });
      return;
    }
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditData(undefined);
  };

  const handleSaveSuccess = () => {
    fetchData();
  };

  const handleMallSelect = (mallValue: string) => {
    setSelectedMall(mallValue);
    localStorage.setItem("mall_id", mallValue);
    setIsMallDropdownOpen(false);
    setMallSearchQuery("");
    setPage(1);
  };

  const selectedMallLabel =
    mallOptions.find((opt) => opt.value === selectedMall)?.label ||
    "Select Mall";
  const isMallDisabled = mallOptions.length === 1;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".mall-dropdown-container")) {
        setIsMallDropdownOpen(false);
        setMallSearchQuery("");
      }
    };
    if (isMallDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMallDropdownOpen]);

  const emptyMessage = !selectedMall
    ? "Please select a mall"
    : selectedYear === 0
      ? "Please select a year"
      : selectedMonth === 0
        ? "Please select a month"
        : "No pest control records found";

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-2 lg:col-span-3 mall-dropdown-container relative">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Mall
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    !isMallDisabled &&
                    setIsMallDropdownOpen(!isMallDropdownOpen)
                  }
                  disabled={isMallDisabled}
                  className={cx(
                    "w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100 flex items-center justify-between",
                    isMallDisabled
                      ? "cursor-not-allowed opacity-60"
                      : "cursor-pointer"
                  )}
                >
                  <span className={!selectedMall ? "text-gray-400" : ""}>
                    {selectedMallLabel}
                  </span>
                  <ChevronDown
                    className={cx(
                      "w-4 h-4 text-gray-400 transition-transform",
                      isMallDropdownOpen && "rotate-180"
                    )}
                  />
                </button>

                {isMallDropdownOpen && !isMallDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={mallSearchQuery}
                          onChange={(e) => setMallSearchQuery(e.target.value)}
                          placeholder="Search mall..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredMallOptions.length > 0 ? (
                        filteredMallOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleMallSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedMall === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
                          No mall found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="sm:col-span-1 lg:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setPage(1);
                }}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              >
                <option value={0} disabled>
                  Select Year
                </option>
                {generateYearOptions().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-1 lg:col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(parseInt(e.target.value));
                  setPage(1);
                }}
                className="w-full px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              >
                <option value={0} disabled>
                  Select Month
                </option>
                {monthNames.map((month, index) => (
                  <option key={index + 1} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search"
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100 placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="sm:col-span-2 lg:col-span-2">
              <label className="hidden lg:block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                &nbsp;
              </label>
              <button
                onClick={handleAddNew}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </p>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="py-3 px-4 text-left">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          No
                        </span>
                      </th>
                      <Th
                        sortable
                        activeKey={sortKey}
                        dir={sortDir}
                        k="TenantName"
                        onClick={toggleSort}
                      >
                        Store
                      </Th>
                      <Th
                        sortable
                        activeKey={sortKey}
                        dir={sortDir}
                        k="VendorName"
                        onClick={toggleSort}
                      >
                        Vendor Name
                      </Th>
                      <th className="py-3 px-4 text-left">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Contact Number
                        </span>
                      </th>
                      <Th
                        sortable
                        activeKey={sortKey}
                        dir={sortDir}
                        k="ControlOn"
                        onClick={toggleSort}
                      >
                        Maintenance Date
                      </Th>
                      <th className="py-3 px-4 text-left">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Notes
                        </span>
                      </th>
                      <th className="py-3 px-4 text-center">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Actions
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          {emptyMessage}
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((item, idx) => (
                        <tr
                          key={item.PestControlId}
                          className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                        >
                          <td className="py-4 px-4 text-sm text-gray-900 dark:text-gray-100">
                            {start + idx + 1}
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {item.TenantName || "-"}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                            {item.VendorName || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                            {item.VendorPhone || "-"}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">
                            {formatDate(item.ControlOn)}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-500 dark:text-gray-400">
                            {item.Notes || "-"}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(item)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-900 hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300 transition-colors"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(item.PestControlId)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">
                {pageRows.length === 0 ? (
                  <div className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {emptyMessage}
                  </div>
                ) : (
                  pageRows.map((item, idx) => (
                    <div
                      key={item.PestControlId}
                      className="p-4 space-y-3 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-xs font-semibold flex items-center justify-center">
                          {start + idx + 1}
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {item.TenantName || "-"}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                            Vendor Name
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {item.VendorName || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                            Contact Number
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {item.VendorPhone || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                            Maintenance Date
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {formatDate(item.ControlOn)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                            Notes
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {item.Notes || "-"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.PestControlId)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Show result:
              </span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={clampedPage === 1}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === 1
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                )}
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (clampedPage <= 3) {
                  pageNum = i + 1;
                } else if (clampedPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = clampedPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cx(
                      "w-9 h-9 text-sm rounded-lg transition-colors",
                      pageNum === clampedPage
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && clampedPage < totalPages - 2 && (
                <>
                  <span className="text-gray-400">...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-9 h-9 text-sm rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={clampedPage === totalPages}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === totalPages
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                )}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      <PestControlForm
        isOpen={isModalOpen}
        onClose={handleModalClose}
        editData={editData}
        onSaveSuccess={handleSaveSuccess}
      />
    </div>
  );
}

function Th({
  children,
  sortable,
  k,
  activeKey,
  dir,
  onClick,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  k?: SortKey;
  activeKey?: SortKey;
  dir?: "asc" | "desc";
  onClick?: (k: SortKey) => void;
}) {
  const isActive = sortable && k === activeKey;
  return (
    <th
      className={cx(
        "py-3 px-4 text-left select-none",
        sortable && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
      )}
      onClick={
        sortable && k && onClick ? () => onClick(k as SortKey) : undefined
      }
    >
      <div className="inline-flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {children}
        </span>
        {sortable && (
          <svg
            className={cx(
              "w-3 h-3",
              isActive ? "text-gray-600 dark:text-gray-300" : "text-gray-400"
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            {isActive ? (
              dir === "asc" ? (
                <path d="M5 15l7-7 7 7" />
              ) : (
                <path d="M19 9l-7 7-7-7" />
              )
            ) : (
              <>
                <path d="M5 15l7-7 7 7" />
                <path d="M5 9l7 7 7-7" opacity="0.4" />
              </>
            )}
          </svg>
        )}
      </div>
    </th>
  );
}